import React from 'react';
import styles from '../styles/styles.module.css';
import { confirmSignIn, signOut } from '@aws-amplify/auth';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../Redux/store';
import {
  setAuthenticated,
  setIsLoading,
  setMfaCode,
} from '../../../Redux/slices/authSlice';
import { Navigate, useLocation } from 'react-router-dom'; // 🛠️ Added useNavigate
import { getUser } from '../../../api/user/post';
import { setUser } from '../../../Redux/slices/userSlice';
import { setUsers } from '../../../Redux/slices/allUsers';
import { setSites } from '../../../Redux/slices/allSites';
import { setCurrentSite } from '../../../Redux/slices/currentSiteSlice';
import { setLogEntires } from '../../../Redux/slices/logEntires';
import {
  setChecklistTabItems,
  setInventoryTabItems,
  setReportsTabItems,
  setTrackerTabItems,
} from '../../../Redux/slices/tabSlice';
import {
  setChecklistLayouts,
  setInventoryLayouts,
  setReportLayouts,
  setTrackerLayouts,
} from '../../../Redux/slices/UIBuilderLayouts';
import { setReportDataFromBins } from '../../../Redux/slices/reportDataSlice';
import { setShiftToDoItems } from '../../../Redux/slices/shiftToDoSlice';
import { setVisitorLog } from '../../../Redux/slices/visitorLogSlice';

export const Mfa: React.FC = () => {
  const location = useLocation();

  const { isLoading, mfaCode, errorMessage } = useSelector(
    (state: AppState) => state.auth
  );
  const dispatch = useDispatch();

  async function handleMfaSubmit() {
    dispatch(setIsLoading(true));
    try {
      const user = await confirmSignIn({
        challengeResponse: mfaCode,
      });

      if (user.isSignedIn) {
        const res = await getUser();
        if (res) {
          if (res?.data?.defaultPaymentSession?.url) {
            window.location.href = res.data.defaultPaymentSession.url;
            return;
          }

          const selectedSite = res?.data?.selectedSite ?? {
            siteId: '0000',
            siteName: 'MASTER',
            timezone: 'America/Los_Angeles',
            shiftStart: '06:00',
            siteStreet: '',
            siteCity: '',
            siteState: '',
            siteZip: '',
          };

          if (res.data.reports) {
            dispatch(
              setReportDataFromBins({
                bins: res.data.reports,
                timezone: selectedSite.timezone || 'America/Los_Angeles',
              })
            );
          }

          if (res.data.shiftToDo) {
            if (res.data.shiftToDo.length) {
              dispatch(setShiftToDoItems(res.data.shiftToDo));
            }
          }

          if (res.data.reportCategories) {
            dispatch(setTrackerTabItems(res.data.reportCategories.trackers));
            dispatch(
              setInventoryTabItems(res.data.reportCategories.inventories)
            );
            dispatch(
              setChecklistTabItems(res.data.reportCategories.checklists)
            );
            dispatch(setChecklistLayouts(res.data.reportCategories.checklists));
            dispatch(setTrackerLayouts(res.data.reportCategories.trackers));
            dispatch(
              setInventoryLayouts(res.data.reportCategories.inventories)
            );
            dispatch(setReportLayouts(res.data.reportCategories.reports));
            dispatch(setReportsTabItems(res.data.reportCategories.reports));
          }

          dispatch(setCurrentSite(selectedSite));
          dispatch(setUser(res.data));
          dispatch(setUsers(res.data.associatedUsers));
          dispatch(setSites(res.data.associatedSites));
          dispatch(setAuthenticated(true));
          //no log entries needed for all access user
          if (res.data.associatedCompany !== 'ALL_ACCESS') {
            dispatch(setLogEntires(res.data.logEntries));
            dispatch(setVisitorLog(res?.data.visitorLog));
          }
          <Navigate to="/dashboard" state={{ from: location }} replace />;
        } else {
          dispatch(setAuthenticated(false));
          await signOut();
        }
      } else {
        dispatch(setAuthenticated(false));
        await signOut();
      }
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        if (err.message.toLowerCase().includes('invalid session')) {
          //Critical session error
          await signOut();
          dispatch(setAuthenticated(false));
          alert('Session expired. Please start over.');
          window.location.reload();
        } else {
          console.log(err);
          // Regular MFA failure
          await signOut();
          dispatch(setAuthenticated(false));
          alert('Failed to confirm MFA code. Please try logging in again.'); // ✅
          window.location.reload();
        }
      } else {
        await signOut();
        dispatch(setAuthenticated(false));
        alert('An unexpected error occurred. Please try logging in again.'); // ✅
        window.location.reload();
      }
    } finally {
      dispatch(setIsLoading(false));
      dispatch(setMfaCode('')); // clear MFA code
    }
  }

  return (
    <>
      <div className={styles.inputContainer}>
        <p className={styles.label}>MFA Code</p>
        <input
          onChange={(e) => dispatch(setMfaCode(e.target.value))}
          value={mfaCode}
          className={styles.inputElement}
          type="text"
          required
          autoFocus
        />
      </div>

      <div className={styles.errorContainer}>
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </div>

      <div className={styles.buttonContainer}>
        <button
          onClick={handleMfaSubmit}
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? 'Please wait ...' : 'Verify MFA'}
        </button>
      </div>
    </>
  );
};
